import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { DesktopAdminLayout } from '@/components/common/admin/desktop/DesktopAdminLayout';
import { DesktopPageHeader } from '@/components/common/admin/desktop/DesktopPageHeader';
import { GlassCard } from '@/components/shared/redesign';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import {
  useGetSurveyResponsesQuery,
  useGetSurveyResultsQuery,
} from '@/store/api/corkBoard/corkBoardApi';
import {
  useGetSurveySentimentQuery,
  usePostReclassifySurveyMutation,
} from '@/store/api/sentiment/sentimentApi';
import { SENTIMENT_POLL_INTERVAL_MS } from '@/constants/sentimentConstants';
import { SentimentBadge } from '@/components/admin/corkBoard/sentiment/SentimentBadge';
import { QuestionSentimentCard } from '@/components/admin/corkBoard/sentiment/QuestionSentimentCard';
import { computeAwaiting } from '@/components/admin/corkBoard/sentiment/computeAwaiting';
import type {
  QuestionResult,
  SurveyResponseItem,
} from '@/types/residents/corkBoard';
import { SurveyResultsView } from '@/components/admin/corkBoard/survey/SurveyResultsView';
import type { SurveySentimentSnapshot } from '@/types/sentiment';

interface Props {
  threadId: string;
}

const TABLE_PADDING = 16;
const ID_COL_WIDTH = 180;
const MIN_QUESTION_COL_WIDTH = 220;

function hasPendingSentiment(
  snap: SurveySentimentSnapshot | undefined,
): boolean {
  if (!snap) return true;
  return (
    snap.answers.some(a => a.status === 'pending') ||
    snap.questions.some(q => q.status === 'pending')
  );
}

export const DesktopSurveyResponsesView: React.FC<Props> = ({ threadId }) => {
  const navigation = useNavigation();
  const { semantic } = useAdminColors();
  const [page, setPage] = useState(0);
  const numericThreadId = Number(threadId);
  const { data: results } = useGetSurveyResultsQuery(threadId);
  const { data, isLoading } = useGetSurveyResponsesQuery({
    threadId,
    page,
    pageSize: 50,
  });

  // pollingInterval reads from a ref so we don't trigger a hook-restart loop every
  // render. The ref is synced to the latest snapshot via useEffect (post-render).
  const sentimentRef = useRef<SurveySentimentSnapshot | undefined>(undefined);
  const { data: sentiment } = useGetSurveySentimentQuery(numericThreadId, {
    pollingInterval: hasPendingSentiment(sentimentRef.current)
      ? SENTIMENT_POLL_INTERVAL_MS
      : 0,
  });
  useEffect(() => {
    sentimentRef.current = sentiment;
  }, [sentiment]);
  const [postReclassify] = usePostReclassifySurveyMutation();

  const longTextQuestions = useMemo(
    () => (results?.questions ?? []).filter(q => q.type === 'long_text'),
    [results],
  );

  const totalCount = data?.pagination.totalCount ?? 0;
  const subtitle = isLoading
    ? 'Loading responses…'
    : `${totalCount} response${totalCount === 1 ? '' : 's'}`;

  return (
    <DesktopAdminLayout>
      {/* The page stacks aggregate charts + sentiment cards + the response
          table; on web DesktopAdminLayout is a fixed windowHeight box, so this
          content must scroll vertically or it clips off-screen. */}
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator
      >
        <TouchableOpacity
          style={styles.backLink}
          onPress={() => navigation.goBack()}
          accessibilityRole="button"
          accessibilityLabel="Back to threads"
        >
          <Text style={[styles.backLinkText, { color: semantic.text.link }]}>
            ‹ Back to threads
          </Text>
        </TouchableOpacity>

        <DesktopPageHeader title="Survey responses" subtitle={subtitle} />

        {/* Aggregate analysis (poll/rating bars) — admin-only, from the
          "Survey Analysis — Admin Only" design. Rendered at the top. */}
        {results && <SurveyResultsView results={results} />}

        {/* AI sentiment summary cards, below the quantitative aggregates and
          above the response table. LAYOUT CHOICE made during the
          sentiment<->admin-survey rebase; pending the returning engineer's
          review — see PR #148. */}
        {longTextQuestions.length > 0 && (
          <View style={styles.sentimentCardsWrap}>
            {longTextQuestions.map(q => (
              <QuestionSentimentCard
                key={q.questionId}
                questionId={q.questionId}
                question={sentiment?.questions.find(
                  s => s.questionId === q.questionId,
                )}
                awaitingAnalysisCount={computeAwaiting(
                  q.questionId,
                  data?.responses ?? [],
                  sentiment,
                )}
                onReclassify={() =>
                  postReclassify({
                    threadId: numericThreadId,
                    scope: 'question',
                    questionId: q.questionId,
                  })
                }
              />
            ))}
          </View>
        )}

        <GlassCard padding={0} style={styles.card}>
          {!data || !results ? (
            <View style={styles.centered}>
              <ActivityIndicator color={semantic.text.link} />
            </View>
          ) : (
            <ResponsesTable
              responses={data.responses}
              cols={results.questions}
              page={page}
              totalPages={data.pagination.totalPages}
              onPrev={() => setPage(p => Math.max(0, p - 1))}
              onNext={() =>
                setPage(p => (p + 1 < data.pagination.totalPages ? p + 1 : p))
              }
              sentiment={sentiment}
            />
          )}
        </GlassCard>
      </ScrollView>
    </DesktopAdminLayout>
  );
};

const ResponsesTable: React.FC<{
  responses: SurveyResponseItem[];
  cols: QuestionResult[];
  page: number;
  totalPages: number;
  onPrev: () => void;
  onNext: () => void;
  sentiment: SurveySentimentSnapshot | undefined;
}> = ({ responses, cols, page, totalPages, onPrev, onNext, sentiment }) => {
  const { admin } = useAdminColors();
  const [containerWidth, setContainerWidth] = useState(0);

  // Stretch question columns to fill the container when there's room; once the
  // table's natural width exceeds the container, columns stay at their minimum
  // and the table scrolls horizontally instead.
  const available = containerWidth - TABLE_PADDING * 2 - ID_COL_WIDTH * 2;
  const colWidth =
    cols.length > 0 && available > 0
      ? Math.max(MIN_QUESTION_COL_WIDTH, available / cols.length)
      : MIN_QUESTION_COL_WIDTH;

  return (
    <View
      style={styles.tableRoot}
      onLayout={e => setContainerWidth(e.nativeEvent.layout.width)}
    >
      <ScrollView horizontal contentContainerStyle={styles.tableWrap}>
        <View style={styles.tableInner}>
          <View
            style={[
              styles.row,
              styles.headerRow,
              {
                backgroundColor: admin.dataTable.headerBg,
                borderColor: admin.dataTable.border,
              },
            ]}
          >
            <Text
              style={[
                styles.headerCell,
                styles.idCell,
                { color: admin.dataCell.textMuted },
              ]}
            >
              Responder
            </Text>
            <Text
              style={[
                styles.headerCell,
                styles.idCell,
                { color: admin.dataCell.textMuted },
              ]}
            >
              Submitted
            </Text>
            {cols.map(q => (
              <Text
                key={q.questionId}
                style={[
                  styles.headerCell,
                  { width: colWidth, color: admin.dataCell.textMuted },
                ]}
                numberOfLines={1}
              >
                {q.prompt}
              </Text>
            ))}
          </View>

          {responses.length === 0 ? (
            <View style={styles.empty}>
              <Text style={{ color: admin.dataCell.textMuted }}>
                No responses yet.
              </Text>
            </View>
          ) : (
            responses.map((r, i) => (
              <ResponseRow
                key={r.responseId}
                response={r}
                cols={cols}
                colWidth={colWidth}
                zebra={i % 2 === 1}
                sentiment={sentiment}
              />
            ))
          )}
        </View>
      </ScrollView>

      {totalPages > 1 && (
        <View
          style={[
            styles.paginationRow,
            { borderTopColor: admin.dataTable.border },
          ]}
        >
          <TouchableOpacity onPress={onPrev} disabled={page === 0}>
            <Text
              style={[
                styles.pageControl,
                {
                  color:
                    page === 0
                      ? admin.dataCell.textMuted
                      : admin.dataTable.text,
                },
              ]}
            >
              ← Previous
            </Text>
          </TouchableOpacity>
          <Text
            style={[styles.pageStatus, { color: admin.dataCell.textMuted }]}
          >
            Page {page + 1} of {totalPages}
          </Text>
          <TouchableOpacity onPress={onNext} disabled={page + 1 >= totalPages}>
            <Text
              style={[
                styles.pageControl,
                {
                  color:
                    page + 1 >= totalPages
                      ? admin.dataCell.textMuted
                      : admin.dataTable.text,
                },
              ]}
            >
              Next →
            </Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
};

const ResponseRow: React.FC<{
  response: SurveyResponseItem;
  cols: QuestionResult[];
  colWidth: number;
  zebra: boolean;
  sentiment: SurveySentimentSnapshot | undefined;
}> = ({ response, cols, colWidth, zebra, sentiment }) => {
  const { admin } = useAdminColors();
  return (
    <View
      style={[
        styles.row,
        {
          borderColor: admin.dataTable.border,
          backgroundColor: zebra ? admin.dataTable.rowAlt : 'transparent',
        },
      ]}
    >
      <Text
        style={[styles.cell, styles.idCell, { color: admin.dataTable.text }]}
        numberOfLines={2}
      >
        {response.account ? response.account.name : 'Anonymous'}
      </Text>
      <Text
        style={[
          styles.cell,
          styles.idCell,
          { color: admin.dataCell.textMuted },
        ]}
      >
        {new Date(response.submittedAt).toLocaleString()}
      </Text>
      {cols.map(q => {
        const a = response.answers.find(aa => aa.questionId === q.questionId);
        if (
          q.type === 'long_text' &&
          a &&
          a.textValue !== undefined &&
          a.textValue !== null
        ) {
          const sentForAnswer =
            a.id != null
              ? sentiment?.answers.find(s => s.answerId === a.id)
              : undefined;
          return (
            <View
              key={q.questionId}
              style={[styles.cell, styles.textWithBadge, { width: colWidth }]}
            >
              <Text
                style={[styles.textValueFlex, { color: admin.dataTable.text }]}
                numberOfLines={2}
              >
                {a.textValue}
              </Text>
              {sentForAnswer && (
                <SentimentBadge
                  status={sentForAnswer.status}
                  label={sentForAnswer.label}
                  confidence={sentForAnswer.confidence}
                  errorReason={sentForAnswer.errorReason}
                />
              )}
            </View>
          );
        }
        let cellText = '—';
        if (a) {
          if (q.type === 'poll' && a.optionId !== undefined) {
            cellText =
              q.pollAggregate?.find(p => p.optionId === a.optionId)?.label ??
              `#${a.optionId}`;
          } else if (q.type === 'rating' && a.ratingValue !== undefined) {
            cellText = String(a.ratingValue);
          } else if (q.type === 'long_text' && a.textValue !== undefined) {
            cellText = a.textValue ?? '—';
          }
        }
        return (
          <Text
            key={q.questionId}
            style={[
              styles.cell,
              { width: colWidth, color: admin.dataTable.text },
            ]}
            numberOfLines={2}
          >
            {cellText}
          </Text>
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  backLink: {
    paddingVertical: 4,
    alignSelf: 'flex-start',
  },
  backLinkText: {
    fontSize: 14,
    fontWeight: '600',
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 24,
  },
  card: {
    marginTop: 8,
  },
  centered: {
    flex: 1,
    minHeight: 200,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sentimentCardsWrap: {
    gap: 8,
    marginTop: 8,
  },
  tableRoot: {
    flex: 1,
  },
  tableWrap: {
    padding: TABLE_PADDING,
    minWidth: '100%',
  },
  tableInner: {
    flexGrow: 1,
  },
  row: {
    flexDirection: 'row',
    borderBottomWidth: StyleSheet.hairlineWidth,
    paddingVertical: 10,
  },
  headerRow: {
    borderBottomWidth: 1,
  },
  headerCell: {
    width: MIN_QUESTION_COL_WIDTH,
    paddingHorizontal: 12,
    fontSize: 12,
    fontWeight: '600',
  },
  cell: {
    width: MIN_QUESTION_COL_WIDTH,
    paddingHorizontal: 12,
    fontSize: 14,
  },
  idCell: {
    width: ID_COL_WIDTH,
  },
  empty: {
    padding: 32,
    alignItems: 'center',
  },
  textWithBadge: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  textValueFlex: {
    flex: 1,
    fontSize: 14,
  },
  paginationRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
  },
  pageControl: {
    fontSize: 13,
    fontWeight: '600',
  },
  pageStatus: {
    fontSize: 13,
  },
});
